import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { cx } from '@/styled-system/css';
import StaticMap from 'app/shared/modules/map/StaticMap';
import { mapImageOverlay, mapImageVisible, miniMapContainer, pinOverlay, shimmerSkeleton } from './miniMap.styles';

type MiniMapProps = {
  lat: number;
  lon: number;
  size?: number;
};

type StaticMapOptions = {
  width: number;
  height: number;
  lat: number;
  lon: number;
  zoom: number;
  scale: number;
};

type SignedUrlResult = {
  data?: { signedUrl?: string };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AppDispatch = (action: any) => any;

const buildStyledStaticMap = (options: StaticMapOptions) => {
  return (dispatch: AppDispatch) => {
    // StaticMap is an untyped legacy JS constructor
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const staticMap = new (StaticMap as any)({});

    staticMap
      .size(options.width, options.height)
      .zoom(options.zoom)
      .scale(options.scale)
      .center(options.lat, options.lon)
      .style({ feature: 'water', element: 'all', color: '0x9dced1', visibility: 'on' })
      .style({ feature: 'landscape', element: 'all', visibility: 'simplified' })
      .style({ feature: 'landscape.man_made', element: 'all', visibility: 'on', hue: '0xf0e0c8', gamma: '1.75' })
      .style({ feature: 'poi', element: 'geometry', color: '0xedebdf', visibility: 'on' })
      .style({ feature: 'poi.park', element: 'geometry', color: '0xd8ebd2' })
      .style({ feature: 'road.highway', element: 'geometry.fill', color: '0xffe7c5' })
      .style({ feature: 'road.highway', element: 'geometry.stroke', color: '0xfadbaf' })
      .style({ feature: 'road.arterial', element: 'labels.text.fill', lightness: '25' })
      .style({ feature: 'road.local', element: 'labels.text.fill', lightness: '25' })
      .style({ feature: 'road', element: 'geometry.stroke', weight: '1', visibility: 'on' })
      .style({ feature: 'administrative', element: 'labels.text.fill', color: '0x888888' });

    return dispatch(staticMap.generate()) as Promise<SignedUrlResult>;
  };
};

const isValidCoordinate = (lat: number, lon: number): boolean => {
  return (
    typeof lat === 'number' &&
    typeof lon === 'number' &&
    isFinite(lat) &&
    isFinite(lon) &&
    lat >= -90 &&
    lat <= 90 &&
    lon >= -180 &&
    lon <= 180
  );
};

const PlaceholderSvg = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} xmlns="http://www.w3.org/2000/svg">
    <rect width="100%" height="100%" fill="#f0f8ff" />
    <g opacity="0.3">
      <line x1="0" y1="14" x2={size} y2="14" stroke="#ddd" strokeWidth="2" />
      <line x1="0" y1="28" x2={size} y2="28" stroke="#ddd" strokeWidth="1" />
      <line x1="14" y1="0" x2="14" y2={size} stroke="#ddd" strokeWidth="2" />
      <line x1="30" y1="0" x2="30" y2={size} stroke="#ddd" strokeWidth="1" />
    </g>
    <rect x="6" y="4" width="12" height="6" fill="#9dced1" opacity="0.7" rx="1" />
    <circle cx="32" cy="10" r="5" fill="#d8ebd2" opacity="0.7" />
  </svg>
);

const PinSvg = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#007A70"
    stroke="white"
    strokeWidth="1"
  >
    <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
    <circle cx="12" cy="10" r="3" fill="white" />
  </svg>
);

export const MiniMap = ({ lat, lon, size = 42 }: MiniMapProps) => {
  const dispatch = useDispatch();
  const [mapUrl, setMapUrl] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!isValidCoordinate(lat, lon)) {
      setMapUrl(null);
      return;
    }

    setHasError(false);
    setImageLoaded(false);

    const thunk = buildStyledStaticMap({ width: size, height: size, lat, lon, zoom: 14, scale: 2 });
    (dispatch as AppDispatch)(thunk)
      .then((result: SignedUrlResult) => {
        if (result?.data?.signedUrl) {
          setMapUrl(result.data.signedUrl);
        }
        return undefined;
      })
      .catch(() => {
        setHasError(true);
      });
  }, [dispatch, lat, lon, size]);

  return (
    <div className={miniMapContainer}>
      {!hasError && <div className={shimmerSkeleton} />}
      {hasError && <PlaceholderSvg size={size} />}
      {mapUrl && !hasError && (
        <img
          src={mapUrl}
          alt="Map location"
          className={cx(mapImageOverlay, imageLoaded && mapImageVisible)}
          onLoad={() => setImageLoaded(true)}
          onError={() => setHasError(true)}
        />
      )}
      {isValidCoordinate(lat, lon) && imageLoaded && (
        <div className={cx(pinOverlay, mapImageVisible)}>
          <PinSvg />
        </div>
      )}
    </div>
  );
};
